import type { ReactNode } from "react"

import React, { useCallback, useEffect, useRef } from "react"

import { MessageLoadingSpinner } from "#components/loadingSpinner/messageLoadingSpinner"

// TODO REPO MIGRATE

/**
 * Generic Infinite Scroll List - uses intersectionObserver api to check for sentinel visibility and trigger fetchNextPage
 *
 * @param hasNextPage whether there is a next page to fetch
 * @param fetchNextPage function to fetch the next page when the sentinel is in view
 * @param children the infinite scroll list content that is loaded
 * @param scrollDetectMargin threshold in pixels for detecting when the sentinel is in view - 100px default - means the sentinel will trigger 100px before it is in view to load in advance
 * @param sentinelLoadTimeout timeout in milliseconds to wait before re-triggering the observer callback after fetching the next page - 100ms default - after fetch next page it waits 100ms to check if the sentinel is still in view and therefore trigger successive page loads --> if pages stop loading and there are still pages to load, you may need to increase this checking time
 * @param className optional class name for the list container
 * @param loadingMessage message to display while loading the next page
 */
export function InfiniteScrollList({
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  children,
  scrollDetectMargin = 100,
  sentinelLoadTimeout = 100,
  className,
  loadingMessage = "Loading...",
}: {
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => Promise<any>
  children: ReactNode
  scrollDetectMargin?: number
  sentinelLoadTimeout?: number
  className?: string
  loadingMessage?: string
}) {
  const sentinelRef = useRef<HTMLDivElement>(null)
  const intersectionObserverRef = useRef<IntersectionObserver>()

  const loadCallback = useCallback(
    async (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
      const entry = entries[0]
      // @ts-ignore
      if (entry.isIntersecting && hasNextPage) {
        // Re-trigger observer callback if after fetching the next page the sentinel is still in view
        // @ts-ignore
        observer.unobserve(entry.target)
        await fetchNextPage()
        // @ts-ignore
        setTimeout(() => intersectionObserverRef.current?.observe(entry.target), sentinelLoadTimeout)
      }
    },
    [fetchNextPage, hasNextPage, sentinelLoadTimeout, intersectionObserverRef],
  )

  useEffect(() => {
    if (!sentinelRef.current || !loadCallback) return

    if (intersectionObserverRef.current) {
      intersectionObserverRef.current.disconnect()
    }

    intersectionObserverRef.current = new IntersectionObserver(loadCallback, { rootMargin: `0px 0px ${scrollDetectMargin}px 0px` })
    intersectionObserverRef.current.observe(sentinelRef.current)

    // Cleanup on unmount
    const intersectionObserverMemRef = intersectionObserverRef.current
    return () => {
      intersectionObserverMemRef.disconnect()
    }
  }, [sentinelRef, loadCallback, scrollDetectMargin])

  return (
    <>
      <section className={className}>
        {children}
        <div className={"pointer-events-none invisible"} ref={sentinelRef} />
        {hasNextPage && isFetchingNextPage && <MessageLoadingSpinner className={"mt-6 w-full flex-row gap-x-4 text-nm font-bold"} message={loadingMessage} />}
      </section>
    </>
  )
}
